import type { Server } from "../data/types";
import { localizeServerName, t } from "../utils/i18n";
import { getMockIconUrl, isGreekName } from "../utils/serverIcons";

interface Props {
  servers: Server[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  favoriteIds: number[];
  isFavorite: (id: number) => boolean;
  onToggleFavorite: (id: number) => void;
}

const TYPE_LABEL: Record<number, string> = {
  0: t("typeSatellite"),
  1: t("typeMeteor"),
};

export function ServerList({
  servers,
  selectedId,
  onSelect,
  favoriteIds,
  isFavorite,
  onToggleFavorite,
}: Props) {
  const grouped = new Map<number, Server[]>();
  for (const s of servers) {
    const arr = grouped.get(s.type) ?? [];
    arr.push(s);
    grouped.set(s.type, arr);
  }
  const types = [...grouped.keys()].sort((a, b) => b - a);

  const favoriteSet = new Set(favoriteIds);
  const favoriteServers = servers
    .filter((s) => favoriteSet.has(s.id))
    .sort((a, b) => b.type - a.type || a.level - b.level);

  const renderItem = (s: Server) => {
    const active = s.id === selectedId;
    const fav = isFavorite(s.id);
    return (
      <li key={s.id} className="flex items-stretch">
        <button
          onClick={() => onSelect(s.id)}
          className={
            "flex flex-1 items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors " +
            (active
              ? "bg-sky-600 text-white"
              : "text-slate-800 hover:bg-slate-100")
          }
        >
          <ServerThumbnail server={s} active={active} />
          <span
            className={
              "shrink-0 tabular-nums " +
              (active ? "text-sky-100" : "text-slate-500")
            }
          >
            Lv{s.level}
          </span>
          <span className="truncate">{localizeServerName(s.name)}</span>
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleFavorite(s.id);
          }}
          aria-label={fav ? t("removeFromFavorites") : t("addToFavorites")}
          aria-pressed={fav}
          className={
            "shrink-0 px-2 text-base transition-colors " +
            (active
              ? "text-yellow-300 hover:text-yellow-200"
              : fav
                ? "text-yellow-500 hover:text-yellow-600"
                : "text-slate-300 hover:text-slate-500")
          }
        >
          {fav ? "★" : "☆"}
        </button>
      </li>
    );
  };

  return (
    <aside className="h-full w-64 shrink-0 overflow-y-auto border-r border-slate-300 bg-white">
      {favoriteServers.length > 0 && (
        <section>
          <h2 className="sticky top-0 z-10 bg-yellow-100 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-yellow-800">
            {t("favorites")}
          </h2>
          <ul>{favoriteServers.map(renderItem)}</ul>
        </section>
      )}
      {types.map((t) => (
        <section key={t}>
          <h2 className="sticky top-0 z-10 bg-slate-200 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-700">
            {TYPE_LABEL[t] ?? `Type ${t}`}
          </h2>
          <ul>{grouped.get(t)!.map(renderItem)}</ul>
        </section>
      ))}
    </aside>
  );
}

function ServerThumbnail({ server, active }: { server: Server; active: boolean }) {
  const useMockPriority = isGreekName(server.name);
  const mockUrl = getMockIconUrl(server.name, server.type);
  const primarySrc = useMockPriority
    ? mockUrl
    : (server.v3ImagePath ?? server.imagePath);
  const fallbackSrc = useMockPriority
    ? (server.v3ImagePath ?? server.imagePath)
    : mockUrl;

  return (
    <div
      className={
        "flex h-6 w-6 shrink-0 items-center justify-center rounded bg-white " +
        (active ? "ring-1 ring-white/50" : "ring-1 ring-slate-200")
      }
    >
      <img
        src={primarySrc}
        alt=""
        className="h-full w-full object-contain"
        style={!useMockPriority && server.v3ImagePath ? { imageRendering: "pixelated" } : undefined}
        onError={(e) => {
          const img = e.currentTarget;
          if (img.src !== fallbackSrc) {
            img.src = fallbackSrc;
          }
        }}
      />
    </div>
  );
}
